import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../services/api";
import { useAuth } from "../../context/AuthContext";
import {
  PageContainer, PageHeader, Card, CardHeader,
  Button, FormGroup, Input, Select, Muted,
} from "../ui";

interface Vendor {
  id: string;
  name: string;
}

interface PricingSettings {
  card_markup_percent: number;
  sales_tax_rate: number;
  default_rounding_strategy: string;
}

interface MatrixCell {
  category: string;
  package_type: string;
  margin_percent: number;
}

interface FormatState {
  id?: string;
  name: string;
  package_type: string;
  oz_per_unit: string;
  units_per_case: string;
  upc: string;
  pricing_mode: string;
  cash_price: string;
  card_price: string;
  upc_not_needed?: boolean;
}

function computeCostPerOz(costPerCase: string, ozPerCase: string): number {
  const cost = Number(costPerCase);
  const oz = Number(ozPerCase);
  return oz > 0 ? cost / oz : 0;
}

function computeMargin(cashPrice: number, costBasis: number): number {
  if (cashPrice <= 0) return 0;
  return ((cashPrice - costBasis) / cashPrice) * 100;
}

function applyRounding(price: number, strategy: string): number {
  switch (strategy) {
    case "nearest_5_cents":
      return Math.round(price * 20) / 20;
    case "nearest_25_cents":
      return Math.round(price * 4) / 4;
    case "nearest_99_cents":
      return Math.floor(price) + 0.99;
    case "none":
    default:
      return Math.round(price * 100) / 100;
  }
}

function MarginInput({ costBasis, cashPrice, currentMargin, inputClass, inputStyle, focusHandlers, onChange }: {
  costBasis: number;
  cashPrice: number;
  currentMargin: number;
  inputClass: string;
  inputStyle: React.CSSProperties;
  focusHandlers: { onFocus: (e: React.FocusEvent<HTMLInputElement>) => void; onBlur: (e: React.FocusEvent<HTMLInputElement>) => void };
  onChange: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");
  const displayValue = costBasis > 0 && cashPrice > 0 ? currentMargin.toFixed(1) : "";

  return (
    <input
      type="text"
      inputMode="decimal"
      value={editing ? editValue : displayValue}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" || /^\d*\.?\d*$/.test(v)) setEditValue(v);
      }}
      onFocus={(e) => {
        setEditing(true);
        setEditValue(displayValue);
        focusHandlers.onFocus(e);
      }}
      onBlur={(e) => {
        if (editValue && editValue !== displayValue) onChange(editValue);
        setEditing(false);
        focusHandlers.onBlur(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (editValue) onChange(editValue);
          setEditing(false);
          (e.target as HTMLInputElement).blur();
        }
      }}
      className={inputClass}
      style={{
        ...inputStyle,
        fontWeight: 600,
        color: currentMargin < 15 ? "#dc2626" : currentMargin < 25 ? "#d97706" : "#16a34a",
      }}
      placeholder="-"
    />
  );
}

function TaxPriceInput({ priceWithTax, inputClass, inputStyle, focusHandlers, onChange }: {
  priceWithTax: string;
  inputClass: string;
  inputStyle: React.CSSProperties;
  focusHandlers: { onFocus: (e: React.FocusEvent<HTMLInputElement>) => void; onBlur: (e: React.FocusEvent<HTMLInputElement>) => void };
  onChange: (value: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [editValue, setEditValue] = useState("");

  return (
    <input
      type="text"
      inputMode="decimal"
      value={editing ? editValue : priceWithTax}
      onChange={(e) => {
        const v = e.target.value;
        if (v === "" || /^\d*\.?\d*$/.test(v)) setEditValue(v);
      }}
      onFocus={(e) => {
        setEditing(true);
        setEditValue(priceWithTax);
        focusHandlers.onFocus(e);
      }}
      onBlur={(e) => {
        if (editValue && editValue !== priceWithTax) onChange(editValue);
        setEditing(false);
        focusHandlers.onBlur(e);
      }}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.preventDefault();
          if (editValue) onChange(editValue);
          setEditing(false);
          (e.target as HTMLInputElement).blur();
        }
      }}
      className={inputClass}
      style={{ ...inputStyle, fontWeight: 600 }}
      placeholder="0.00"
    />
  );
}

export default function ProductForm() {
  const { user } = useAuth();
  const isManager = user?.role === "owner" || user?.role === "manager";
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;

  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [pricingSettings, setPricingSettings] = useState<PricingSettings | null>(null);
  const [matrixCells, setMatrixCells] = useState<MatrixCell[]>([]);
  const [form, setForm] = useState({
    name: "",
    brand: "",
    description: "",
    type: "liquid",
    category: "domestic",
    case_configuration: "",
    vendor_id: "",
    case_sku: "",
    oz_per_case: "",
    current_cost_per_case: "",
    tax_category: "standard",
  });
  const [formats, setFormats] = useState<FormatState[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/vendors").then((r) => setVendors(r.data));
    api.get("/pricing/settings").then((r) => setPricingSettings(r.data));
    api.get("/pricing/matrix").then((r) => setMatrixCells(r.data));
    if (isEdit) {
      api.get(`/products/${id}`).then((r) => {
        const p = r.data;
        setForm({
          name: p.name || "",
          brand: p.brand || "",
          description: p.description || "",
          type: p.type,
          category: p.category,
          case_configuration: p.case_configuration || "",
          vendor_id: p.vendor_id || "",
          case_sku: p.case_sku || "",
          oz_per_case: String(p.oz_per_case),
          current_cost_per_case: String(p.current_cost_per_case),
          tax_category: p.tax_category || "standard",
        });
        if (p.sellable_formats) {
          setFormats(p.sellable_formats.filter((f: Record<string, unknown>) => f.is_active !== false).map((f: Record<string, unknown>) => ({
            id: f.id as string,
            name: f.name as string || "",
            package_type: f.package_type as string || "",
            oz_per_unit: String(f.oz_per_unit),
            units_per_case: String(f.units_per_case || ""),
            upc: (f.upc as string) || "",
            pricing_mode: f.pricing_mode as string || "matrix",
            cash_price: String(f.cash_price),
            card_price: String(f.card_price),
            upc_not_needed: Boolean(f.upc_not_needed),
          })));
        }
      });
    }
  }, [id, isEdit]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const productData = {
        ...form,
        oz_per_case: Number(form.oz_per_case),
        current_cost_per_case: Number(form.current_cost_per_case),
        vendor_id: form.vendor_id || null,
      };

      let productId = id;
      if (isEdit) {
        await api.put(`/products/${id}`, productData);
      } else {
        const response = await api.post("/products", productData);
        productId = response.data.id;

        // Create receive format
        await api.post(`/products/${productId}/receive-formats`, {
          name: "Case",
          oz_per_unit: Number(form.oz_per_case),
          default_cost_per_unit: Number(form.current_cost_per_case),
        });
      }

      // Save formats
      for (const fmt of formats) {
        if (fmt.id) {
          await api.put(`/products/sellable-formats/${fmt.id}`, {
            name: fmt.name,
            package_type: fmt.package_type,
            oz_per_unit: Number(fmt.oz_per_unit),
            units_per_case: fmt.units_per_case ? Number(fmt.units_per_case) : null,
            upc: fmt.upc || null,
            pricing_mode: fmt.pricing_mode,
            cash_price: Number(fmt.cash_price),
            card_price: Number(fmt.card_price),
          });
        } else {
          await api.post(`/products/${productId}/sellable-formats`, {
            name: fmt.name,
            package_type: fmt.package_type,
            oz_per_unit: Number(fmt.oz_per_unit),
            units_per_case: fmt.units_per_case ? Number(fmt.units_per_case) : null,
            upc: fmt.upc || null,
            pricing_mode: fmt.pricing_mode,
            cash_price: Number(fmt.cash_price),
            card_price: Number(fmt.card_price),
          });
        }
      }

      navigate("/products");
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Patrick, 10/6: remove a size that should not be on this product. A size
  // that has sold is switched off (history keeps resolving); one that never
  // sold is deleted. Unsaved rows just disappear.
  const removeFormat = async (idx: number) => {
    const fmt = formats[idx];
    const label = fmt.name || fmt.package_type || "this format";
    if (!window.confirm(`Remove "${label}" from this product? The register will no longer offer it.`)) return;
    if (fmt.id) {
      try {
        await api.delete(`/products/sellable-formats/${fmt.id}`);
      } catch (err) {
        alert("Could not remove the format: " + (err instanceof Error ? err.message : String(err)));
        return;
      }
    }
    setFormats(formats.filter((_, i) => i !== idx));
  };

  const addFormat = () => {
    setFormats([...formats, {
      name: "", package_type: "single", oz_per_unit: "", units_per_case: "",
      upc: "", pricing_mode: "matrix", cash_price: "0", card_price: "0",
    }]);
  };

  const updateFormat = (idx: number, field: string, value: string) => {
    const updated = [...formats];
    (updated[idx] as unknown as Record<string, string>)[field] = value;
    setFormats(updated);
  };

  const getMatrixMargin = (packageType: string): number | null => {
    const cell = matrixCells.find(
      (c) => c.category === form.category && c.package_type === packageType
    );
    return cell ? Number(cell.margin_percent) : null;
  };

  const getMatrixPrice = (fmt: FormatState): { cash: number; card: number } | null => {
    if (!pricingSettings) return null;
    const margin = getMatrixMargin(fmt.package_type);
    if (margin === null) return null;
    const costPerOz = computeCostPerOz(form.current_cost_per_case, form.oz_per_case);
    const ozPerUnit = Number(fmt.oz_per_unit);
    if (costPerOz <= 0 || ozPerUnit <= 0) return null;
    const costBasis = costPerOz * ozPerUnit;
    const rawCash = costBasis / (1 - margin / 100);
    const cash = applyRounding(rawCash, pricingSettings.default_rounding_strategy);
    const card = applyRounding(cash * (1 + pricingSettings.card_markup_percent / 100), pricingSettings.default_rounding_strategy);
    return { cash, card };
  };

  const applyMatrixPrice = (idx: number) => {
    const fmt = formats[idx];
    const prices = getMatrixPrice(fmt);
    if (prices) {
      const updated = [...formats];
      updated[idx] = { ...updated[idx], cash_price: String(prices.cash), card_price: String(prices.card), pricing_mode: "matrix" };
      setFormats(updated);
    }
  };

  const handleCashPriceChange = (idx: number, value: string) => {
    const cashPrice = Number(value);
    const updated = [...formats];
    updated[idx] = { ...updated[idx], cash_price: value, pricing_mode: "manual" };
    if (pricingSettings && cashPrice > 0) {
      const cardPrice = Math.round(cashPrice * (1 + pricingSettings.card_markup_percent / 100) * 100) / 100;
      updated[idx].card_price = String(cardPrice);
    }
    setFormats(updated);
  };

  const handleMarginChange = (idx: number, marginStr: string) => {
    const margin = Number(marginStr);
    if (!pricingSettings || isNaN(margin)) return;
    const fmt = formats[idx];
    const costPerOz = computeCostPerOz(form.current_cost_per_case, form.oz_per_case);
    const ozPerUnit = Number(fmt.oz_per_unit);
    if (costPerOz <= 0 || ozPerUnit <= 0 || margin >= 100) return;
    const costBasis = costPerOz * ozPerUnit;
    const rawCash = costBasis / (1 - margin / 100);
    const cashPrice = Math.round(rawCash * 100) / 100;
    const cardPrice = Math.round(cashPrice * (1 + pricingSettings.card_markup_percent / 100) * 100) / 100;
    const updated = [...formats];
    updated[idx] = { ...updated[idx], cash_price: String(cashPrice), card_price: String(cardPrice), pricing_mode: "manual" };
    setFormats(updated);
  };

  const handlePriceWithTaxChange = (idx: number, value: string) => {
    const priceWithTax = Number(value);
    if (!pricingSettings || priceWithTax <= 0) return;
    const taxRate = Number(pricingSettings.sales_tax_rate);
    const cashPrice = Math.round(priceWithTax / (1 + taxRate) * 100) / 100;
    const cardPrice = Math.round(cashPrice * (1 + pricingSettings.card_markup_percent / 100) * 100) / 100;
    const updated = [...formats];
    updated[idx] = { ...updated[idx], cash_price: String(cashPrice), card_price: String(cardPrice), pricing_mode: "manual" };
    setFormats(updated);
  };

  const categories = [
    "domestic", "import", "craft", "seltzer", "rtd", "malternative", "cider", "na", "accessory", "other"
  ];
  const packageTypes = [
    "case_full", "12pk", "8pk", "6pk", "4pk", "single", "slushie", "growler", "crowler", "pint", "taster"
  ];

  const inputStyle = { border: "2px solid var(--rbw-border)", color: "var(--rbw-navy)", background: "var(--rbw-bg-card)" };
  const inputClass = "px-3 py-2 rounded-xl text-sm outline-none w-full";
  const focusHandlers = {
    onFocus: (e: React.FocusEvent<HTMLInputElement>) => (e.target.style.borderColor = "var(--rbw-blue)"),
    onBlur: (e: React.FocusEvent<HTMLInputElement>) => (e.target.style.borderColor = "var(--rbw-border)"),
  };

  return (
    <PageContainer>
      <PageHeader
        title={isEdit ? "Edit Product" : "New Product"}
        subtitle={isEdit ? "Update product details and formats" : "Add a new product to your catalog"}
        backTo="/products"
        action={isEdit ? (
          <Button onClick={async () => {
            try {
              const res = await api.post(`/products/${id}/duplicate`);
              navigate(`/products/${res.data.id}`);
            } catch { alert("Failed to duplicate"); }
          }}>
            Duplicate Product
          </Button>
        ) : undefined}
      />

      <form onSubmit={handleSubmit} className="space-y-6 max-w-4xl">
        <Card>
          <CardHeader title="Product Details" />
          <div className="grid grid-cols-2 gap-4">
            <FormGroup label="Name" span={2}>
              <Input
                value={form.name}
                onChange={(v) => setForm({ ...form, name: v })}
                placeholder="Modelo Especial 2/12 12oz Cans"
                required
              />
            </FormGroup>
            <FormGroup label="Brand">
              <Input
                value={form.brand}
                onChange={(v) => setForm({ ...form, brand: v })}
                placeholder="Modelo Especial"
              />
            </FormGroup>
            <FormGroup label="Category">
              <Select value={form.category} onChange={(v) => setForm({ ...form, category: v })}>
                {categories.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Case Configuration">
              <Input
                value={form.case_configuration}
                onChange={(v) => setForm({ ...form, case_configuration: v })}
                placeholder="2/12"
              />
            </FormGroup>
            <FormGroup label="Vendor">
              <Select value={form.vendor_id} onChange={(v) => setForm({ ...form, vendor_id: v })}>
                <option value="">No vendor</option>
                {vendors.map((v) => (
                  <option key={v.id} value={v.id}>{v.name}</option>
                ))}
              </Select>
            </FormGroup>
            <FormGroup label="Oz per Case">
              <Input
                type="number"
                value={form.oz_per_case}
                onChange={(v) => setForm({ ...form, oz_per_case: v })}
                placeholder="288"
                required
              />
            </FormGroup>
            {isManager && <FormGroup label="Cost per Case">
              <Input
                type="number"
                step="0.01"
                value={form.current_cost_per_case}
                onChange={(v) => setForm({ ...form, current_cost_per_case: v })}
                placeholder="26.00"
              />
            </FormGroup>}
            <FormGroup label="Case SKU/UPC">
              <Input
                value={form.case_sku}
                onChange={(v) => setForm({ ...form, case_sku: v })}
              />
            </FormGroup>
            <FormGroup label="Type">
              <Select value={form.type} onChange={(v) => setForm({ ...form, type: v })}>
                <option value="liquid">Liquid</option>
                <option value="non_liquid">Non-Liquid</option>
              </Select>
            </FormGroup>
          </div>
        </Card>

        <Card>
          <CardHeader
            title="Sellable Formats"
            action={<Button size="sm" onClick={addFormat}>+ Add Format</Button>}
          />

          {formats.map((fmt, idx) => {
            const costPerOz = computeCostPerOz(form.current_cost_per_case, form.oz_per_case);
            const ozPerUnit = Number(fmt.oz_per_unit);
            const costBasis = costPerOz * ozPerUnit;
            const cashPrice = Number(fmt.cash_price);
            const currentMargin = computeMargin(cashPrice, costBasis);
            const matrixPrices = getMatrixPrice(fmt);
            const matrixMargin = getMatrixMargin(fmt.package_type);
            const taxRate = Number(pricingSettings?.sales_tax_rate || 0);
            const priceWithTax = cashPrice > 0 ? (cashPrice * (1 + taxRate)).toFixed(2) : "";
            const needsPrice = !(cashPrice > 0);
            const needsUpc = !fmt.upc.trim() && !fmt.upc_not_needed;

            return (
              <div
                key={idx}
                className="mb-4 p-4 rounded-xl"
                style={{
                  background: "var(--rbw-bg)",
                  ...(needsPrice ? { border: "2px solid var(--rbw-gold)" } : {}),
                }}
              >
                {needsPrice && (
                  <div className="text-xs font-bold mb-2" style={{ color: "var(--rbw-gold-dark)" }}>
                    ⚠ This format needs a cash price before the register can ring it
                  </div>
                )}
                <div className="flex justify-end mb-1">
                  <button
                    type="button"
                    onClick={() => removeFormat(idx)}
                    className="text-xs font-medium px-2 py-1 rounded"
                    style={{ color: "var(--rbw-danger)", background: "transparent", border: "1px solid var(--rbw-danger)" }}
                    title="Remove this size from the product"
                  >
                    Remove format
                  </button>
                </div>
                {/* Row 1: Basic format info with labels */}
                <div className="grid grid-cols-5 gap-2 mb-3">
                  <div>
                    <label className="block text-xs mb-1" style={{ color: "var(--rbw-muted)" }}>Format Name</label>
                    <input
                      value={fmt.name}
                      onChange={(e) => updateFormat(idx, "name", e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      {...focusHandlers}
                      placeholder="e.g. 50ml Single"
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: "var(--rbw-muted)" }}>Package Type</label>
                    <select
                      value={fmt.package_type}
                      onChange={(e) => updateFormat(idx, "package_type", e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                    >
                      {packageTypes.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: "var(--rbw-muted)" }}>Oz per Unit</label>
                    <input
                      type="number"
                      value={fmt.oz_per_unit}
                      onChange={(e) => updateFormat(idx, "oz_per_unit", e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      {...focusHandlers}
                      placeholder="1.69"
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: needsUpc ? "var(--rbw-gold-dark)" : "var(--rbw-muted)" }}>
                      UPC Barcode{needsUpc ? " — fill me in" : ""}
                    </label>
                    <input
                      value={fmt.upc}
                      onChange={(e) => updateFormat(idx, "upc", e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                      className={inputClass}
                      style={needsUpc ? { ...inputStyle, border: "2px dashed var(--rbw-gold)", background: "#FEF3C7" } : inputStyle}
                      {...focusHandlers}
                      placeholder="Scan or type"
                    />
                  </div>
                  <div>
                    <label className="block text-xs mb-1" style={{ color: "var(--rbw-muted)" }}>Units per Case</label>
                    <input
                      type="number"
                      value={fmt.units_per_case}
                      onChange={(e) => updateFormat(idx, "units_per_case", e.target.value)}
                      className={inputClass}
                      style={inputStyle}
                      {...focusHandlers}
                      placeholder="24"
                    />
                  </div>
                </div>

                {/* Row 2: Pricing controls */}
                <div className="grid grid-cols-4 gap-2 items-end">
                  <div>
                    <label className="block text-xs font-bold mb-1" style={{ color: needsPrice ? "var(--rbw-gold-dark)" : "var(--rbw-navy)" }}>
                      Cash Price{needsPrice ? " — fill me in" : ""}
                    </label>
                    <input
                      type="number"
                      step="0.01"
                      value={fmt.cash_price}
                      onChange={(e) => handleCashPriceChange(idx, e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                      className={inputClass}
                      style={{
                        ...inputStyle,
                        fontWeight: 600,
                        color: "var(--rbw-gold-dark)",
                        ...(needsPrice ? { border: "2px solid var(--rbw-gold)", background: "#FEF3C7" } : {}),
                      }}
                      {...focusHandlers}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: "var(--rbw-navy)" }}>Card Price</label>
                    <input
                      type="number"
                      step="0.01"
                      value={fmt.card_price}
                      onChange={(e) => {
                        const updated = [...formats];
                        updated[idx] = { ...updated[idx], card_price: e.target.value, pricing_mode: "manual" };
                        setFormats(updated);
                      }}
                      onKeyDown={(e) => { if (e.key === "Enter") e.preventDefault(); }}
                      className={inputClass}
                      style={{ ...inputStyle, fontWeight: 600, color: "var(--rbw-blue-dark)" }}
                      {...focusHandlers}
                      placeholder="0.00"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: "var(--rbw-navy)" }}>Margin %</label>
                    <MarginInput
                      costBasis={costBasis}
                      cashPrice={cashPrice}
                      currentMargin={currentMargin}
                      inputClass={inputClass}
                      inputStyle={inputStyle}
                      focusHandlers={focusHandlers}
                      onChange={(val) => handleMarginChange(idx, val)}
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1" style={{ color: "var(--rbw-navy)" }}>Price w/ Tax</label>
                    <TaxPriceInput
                      priceWithTax={priceWithTax}
                      inputClass={inputClass}
                      inputStyle={inputStyle}
                      focusHandlers={focusHandlers}
                      onChange={(val) => handlePriceWithTaxChange(idx, val)}
                    />
                  </div>
                </div>

                {/* Row 3: Matrix suggestion + cost info */}
                <div className="flex items-center justify-between mt-2 text-xs" style={{ color: "var(--rbw-muted)" }}>
                  <div className="flex items-center gap-3">
                    {costBasis > 0 && (
                      <span>Cost: ${costBasis.toFixed(2)}</span>
                    )}
                    {matrixPrices && matrixMargin !== null && (
                      <span>
                        Matrix: ${matrixPrices.cash.toFixed(2)} @ {matrixMargin}%
                        {Math.abs(cashPrice - matrixPrices.cash) > 0.01 && (
                          <button
                            type="button"
                            onClick={() => applyMatrixPrice(idx)}
                            className="ml-1 underline"
                            style={{ color: "var(--rbw-blue)" }}
                          >
                            apply
                          </button>
                        )}
                      </span>
                    )}
                    {!matrixPrices && matrixMargin === null && costBasis > 0 && (
                      <span style={{ color: "#d97706" }}>No matrix rule for {form.category} / {fmt.package_type}</span>
                    )}
                  </div>
                  <span className="px-2 py-0.5 rounded text-xs" style={{
                    background: fmt.pricing_mode === "matrix" ? "var(--rbw-blue)" : "var(--rbw-gold)",
                    color: "white",
                  }}>
                    {fmt.pricing_mode}
                  </span>
                </div>
              </div>
            );
          })}

          {formats.length === 0 && (
            <Muted>No formats yet. Add sellable formats for this product.</Muted>
          )}
        </Card>

        {isManager && <div className="flex gap-3 items-center">
          <Button type="submit" disabled={loading}>
            {loading ? "Saving..." : isEdit ? "Save Changes" : "Create Product"}
          </Button>
          <Button variant="ghost" onClick={() => navigate("/products")}>Cancel</Button>
          {isEdit && (
            <button
              type="button"
              onClick={async () => {
                if (!window.confirm(`Delete "${form.name}"? It will be hidden from the catalog and register.`)) return;
                try {
                  await api.delete(`/products/${id}`);
                  navigate("/products");
                } catch { alert("Failed to delete product"); }
              }}
              className="ml-auto text-sm px-4 py-2 rounded-xl transition-all hover:bg-red-50"
              style={{ color: "#dc2626" }}
            >
              Delete Product
            </button>
          )}
        </div>}
      </form>
    </PageContainer>
  );
}
